import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { intervalToDuration } from 'date-fns'
import { getTimezoneOffset } from 'date-fns-tz'

import { IANA_TZ_OPTIONS } from '@makyoapp/makyo-common'
import { ChevronRight, Datetime16 } from '@makyoapp/makyo-icon/icon'
import { DD, SearchBox, useTheme } from '@makyoapp/makyo-ui'

import { NOW } from '@/shared/constants'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

function getTimezoneUTC(tz: string) {
  const tzMs = getTimezoneOffset(tz, NOW)
  const plusOrNeg = tzMs < 0 ? '-' : '+'

  let fmt
  try {
    fmt = intervalToDuration({ start: 0, end: tzMs })
  } catch (e) {
    //
  }
  return `UTC ${plusOrNeg}${String(fmt?.hours).padStart(2, '0')}:${String(fmt?.minutes).padStart(2, '0')}`
}

export function TimezoneSelect() {
  const { table, activeTable } = useGridV2Context()
  const { state: themeState } = useTheme()
  const { t } = useTranslation()

  const [search, setSearch] = useState('')

  const currentTimezone = table?.tz || (table?.isSnapshot || table?.isQuery ? 'UTC' : 'auto')

  const handleSelectTimezone = (tz: string) => {
    if (!activeTable) return
    activeTable.UpdateTimezone(tz)
  }

  const renderSelectedTimezone = () => {
    if (currentTimezone === 'auto') {
      return (
        <span className='flex-c'>
          <span>Local Time Zone</span>
        </span>
      )
    }

    return (
      <div className='w-full min-w-0 flex-c justify-between'>
        <span className='truncate'>{currentTimezone}</span>
        <span className='shrink-0 text-light text-m-sm'>{getTimezoneUTC(currentTimezone)}</span>
      </div>
    )
  }

  const filteredTzOptions = useMemo(() => {
    return IANA_TZ_OPTIONS.filter((tz) => tz.toLowerCase().includes(search.toLowerCase()))
  }, [search])

  const showDynamic =
    !table?.isSnapshot &&
    !table?.isQuery &&
    (search.length > 0 ? ['local time zone', 'auto'].includes(search.toLowerCase()) : true)

  const showFixed = search.length > 0 ? filteredTzOptions.length > 0 : true

  return (
    <DD
      className='w-full'
      toggleClassName='mk-ddi mk-dd-row flex-c w-full'
      menuClassName='mk-dd-menu border bdr w-[250px] max-h-[70vh] overflow-y-auto'
      menuDefaultSize={false}
      placement={themeState.dir === 'rtl' ? 'left' : 'right'}
      toggle={() => {
        return (
          <>
            <div className='flex-c w-full'>
              <Datetime16 width={18} height={18} />
              {renderSelectedTimezone()}
            </div>
            <ChevronRight />
          </>
        )
      }}
    >
      <SearchBox
        onSearch={(search) => {
          setSearch(search)
        }}
        onClearSearch={() => setSearch('')}
        noBorder
      />

      <hr className='hr my-2' />

      {showDynamic && (
        <>
          <div className='dd-menu-title text-light'>{t('Dynamic')}</div>
          <div
            className={clsx(
              'flex-c mk-dd-row cursor-pointer',
              currentTimezone === 'auto' ? 'bg-zinc-300 dark:bg-slate-700' : 'mk-bg-hover'
            )}
            onClick={() => {
              handleSelectTimezone('auto')
            }}
          >
            <div className='w-full min-w-0 flex-c justify-between'>
              <span className='truncate'>{t('Local Time Zone')}</span>
            </div>
          </div>
        </>
      )}

      {showFixed && (
        <>
          <div className='mt-2 dd-menu-title text-light'>{t('Fixed')}</div>
          <div>
            {filteredTzOptions.map((tz) => {
              const isSelected = tz === currentTimezone
              return (
                <div
                  key={tz}
                  className={clsx(
                    'flex-c mk-dd-row cursor-pointer',
                    isSelected ? 'bg-zinc-300 dark:bg-slate-700' : 'mk-bg-hover'
                  )}
                  onClick={() => {
                    handleSelectTimezone(tz)
                  }}
                >
                  <div className='w-full min-w-0 flex-c justify-between'>
                    <span className='truncate'>{tz}</span>
                    <span className='shrink-0 text-light text-m-sm'>{getTimezoneUTC(tz)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {!showDynamic && !showFixed && <div className='dd-menu-title text-light'>{t('No results found')}</div>}
    </DD>
  )
}
